import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import TableContainer from '@mui/material/TableContainer';
import { Paper, Table, TableBody } from '@mui/material';
import { AntibodiesListModal } from 'components/OrderForm/components/AntibodiesListModal';
import { useUpdateSlideMetadataMutation } from 'store/apis/slidesApi';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { useSelector } from 'react-redux';
import { slidesListOrderSelector } from 'store/slices/slidesListSlice/slidesListSlice';
import {
  getPayload,
  getSlideMeta,
} from 'components/OrderSlidesList/SlideActionTabs/SlideMetadata/utils';
import { MetadataTableCell } from 'components/OrderSlidesList/SlideActionTabs/SlideMetadata/SlideMetadataTable/MetadataTableCell';
import {
  BACKEND_METADATA_LABELS,
  DISPLAYED_METADATA_LABELS,
  UPDATE_SLIDE_METADATA_ERROR,
} from 'components/OrderSlidesList/SlideActionTabs/constants';
import { ScienceInfoSection } from 'components/OrderSlidesList/SlideActionTabs/SlideMetadata/SlideMetadataTable/ScienceInfoSection';
import { SpecialStainsModal } from 'components/OrderSlidesList/SlideActionTabs/SlideMetadata/SpecialStainsModal';

export const SlideMetaDataTable = ({
  metaData,
  sampleData,
  isInitialTab,
  slide,
  isStaff,
  classes,
}) => {
  const [editingRow, setEditingRow] = useState(null);
  const [isAntibodiesListModalOpen, setIsAntibodiesListModalOpen] =
    useState(false);
  const [isSpecialStainsModalOpen, setIsSpecialStainsModalOpen] =
    useState(false);
  const [rowData, setRowData] = useState(metaData);
  const order = useSelector(slidesListOrderSelector);
  const { control, handleSubmit, reset } = useForm();
  const [updateSlide] = useUpdateSlideMetadataMutation();
  const { showError } = useSnackbar();
  const alias = rowData.find(
    ({ label }) => label === DISPLAYED_METADATA_LABELS.alias,
  );

  useEffect(() => {
    setRowData(metaData);
  }, [metaData]);

  const handleAntibodiesListModalClose = () => {
    setIsAntibodiesListModalOpen(false);
  };

  const handleSpecialStainsModalClose = () => {
    setIsSpecialStainsModalOpen(false);
  };

  const handleSave = async (formData) => {
    const isStainTypeEditing =
      editingRow === DISPLAYED_METADATA_LABELS.stainType &&
      formData[editingRow] === BACKEND_METADATA_LABELS.specialStain;

    const isSpecialStainRequired = !rowData.find(
      ({ label }) => label === DISPLAYED_METADATA_LABELS.specialStain,
    );

    if (isStainTypeEditing && isSpecialStainRequired) {
      const newRow = {
        label: DISPLAYED_METADATA_LABELS.specialStain,
        editable: true,
        isNew: true,
      };
      setRowData([
        ...rowData.map((row) => {
          if (row.label === DISPLAYED_METADATA_LABELS.stainType) {
            return { ...row, value: DISPLAYED_METADATA_LABELS.specialStain };
          }

          return row;
        }),
        newRow,
      ]);

      if (isSpecialStainRequired) {
        setIsSpecialStainsModalOpen(true);
      } else return;
    }

    const data = getPayload(formData);
    if (data) {
      updateSlide({
        slideUUID: slide.uuid,
        data,
      })
        .unwrap()
        .then((updatedSlide) => {
          setRowData(getSlideMeta(updatedSlide, order, isStaff));
        })
        .catch(() => {
          showError(UPDATE_SLIDE_METADATA_ERROR);
        });
    }
    setEditingRow(null);
    reset();
  };

  const handleAddAntibodyToSlide = (antibody) => {
    const displayedData = [...metaData];
    const antibodyIndex = displayedData.findIndex(
      ({ label }) => label === DISPLAYED_METADATA_LABELS.antibody,
    );
    displayedData[antibodyIndex].value = antibody.display_name;
    setRowData(displayedData);

    handleAntibodiesListModalClose();
    handleSave({ Antibody: antibody.uuid });
  };

  const handleAddSpecialStain = (stain) => {
    const displayedData = [...metaData];
    let specialStainIndex = displayedData.findIndex(
      ({ label }) => label === DISPLAYED_METADATA_LABELS.specialStain,
    );
    if (specialStainIndex === -1) {
      specialStainIndex =
        displayedData.findIndex(
          ({ label }) => label === DISPLAYED_METADATA_LABELS.stainType,
        ) + 1;
      displayedData.splice(specialStainIndex, 0, {
        label: DISPLAYED_METADATA_LABELS.specialStain,
        editable: true,
        isNew: true,
      });
    }

    displayedData[specialStainIndex].value = stain.name;

    setRowData(displayedData);

    handleSpecialStainsModalClose();
    handleSave({ ...displayedData, SpecialStain: stain.uuid });
  };

  const handleEdit = async (key) => {
    const displayedData = [...metaData];
    const nameIndex = displayedData.findIndex(
      ({ label }) => label === DISPLAYED_METADATA_LABELS.name,
    );
    const newRowForAlias = {
      label: DISPLAYED_METADATA_LABELS.alias,
      editable: true,
      isNew: true,
    };
    switch (key) {
      case DISPLAYED_METADATA_LABELS.specialStain:
        setIsSpecialStainsModalOpen(true);

        return;
      case DISPLAYED_METADATA_LABELS.name:
        if (alias) {
          setEditingRow(DISPLAYED_METADATA_LABELS.alias);

          break;
        }

        displayedData.splice(nameIndex + 1, 0, newRowForAlias);
        setRowData(displayedData);
        setEditingRow(DISPLAYED_METADATA_LABELS.alias);

        return;
      case DISPLAYED_METADATA_LABELS.antibody:
        setIsAntibodiesListModalOpen(true);

        return;
      default:
        break;
    }
    setEditingRow(key);
  };

  const handleCancel = (isNewRow) => {
    if (isNewRow) {
      setRowData(rowData.filter(({ isNew }) => !isNew));
    }
    setEditingRow(null);
    reset();
  };

  return (
    <>
      {!isInitialTab ? (
        <ScienceInfoSection sampleData={sampleData} classes={classes} />
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableBody>
              {rowData.map((data) => (
                <MetadataTableCell
                  key={data.label}
                  {...data}
                  alias={alias}
                  handleEdit={handleEdit}
                  editingRow={editingRow}
                  handleSave={handleSave}
                  handleCancel={handleCancel}
                  handleSubmit={handleSubmit}
                  classes={classes}
                  control={control}
                  isStaff={isStaff}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <AntibodiesListModal
        handleAddToSlide={handleAddAntibodyToSlide}
        isAntibodiesListModalOpen={isAntibodiesListModalOpen}
        handleAntibodiesListModalClose={handleAntibodiesListModalClose}
        isForSlide
      />
      <SpecialStainsModal
        handleSpecialStainsModalClose={handleSpecialStainsModalClose}
        isSpecialStainsModalOpen={isSpecialStainsModalOpen}
        handleAddSpecialStain={handleAddSpecialStain}
        classes={classes}
      />
    </>
  );
};
